import { useContext, useEffect, useMemo, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { AdvancedDragModal, ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { JPBPriceIndexSource } from "./JPBState";
import { JPBManager, JPBPriceIndexFactorsTable } from "./JPBManager";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, XAxis, YAxis } from "recharts";

export type JPBPriceIndexSourceWindowParams = {
   source: JPBPriceIndexSource;
   onConfirm?: (source: JPBPriceIndexSource) => void;
};

const SOURCE_OPTIONS: Array<{ value: JPBPriceIndexSource; label: string }> = [
   { value: JPBPriceIndexSource.AgroCura, label: "AgroCura" },
   { value: JPBPriceIndexSource.Molbak, label: "Mølbak" },
   { value: JPBPriceIndexSource.None, label: "Ingen" }
];

type SourceTableMap = Record<JPBPriceIndexSource, JPBPriceIndexFactorsTable | null>;

type PseudonymizedChartRow = {
   periodKey: string;
   [JPBPriceIndexSource.None]: number | null;
   [JPBPriceIndexSource.Molbak]: number | null;
   [JPBPriceIndexSource.AgroCura]: number | null;
};

function formatFactor(value: number | null): string {
   if (value === null || value === undefined || !Number.isFinite(value)) return "-";
   return value.toFixed(3);
}

function toPseudoFactor(value: number | null | undefined): number | null {
   if (value === null || value === undefined || !Number.isFinite(value)) return null;
   return Math.round(value * 100) / 100;
}

function toPeriodSortValue(periodKey: string): number {
   const q = /^(\d{4})Q([1-4])$/i.exec(periodKey);
   if (q) {
      const year = Number(q[1]);
      const quarter = Number(q[2]);
      return year * 10 + quarter;
   }
   const h = /^(\d{4})H([1-2])$/i.exec(periodKey);
   if (h) {
      const year = Number(h[1]);
      const half = Number(h[2]);
      return year * 10 + half * 2;
   }
   const y = /^(\d{4})$/i.exec(periodKey);
   if (y) {
      return Number(y[1]) * 10;
   }
   return Number.MAX_SAFE_INTEGER;
}

export function JPBPriceIndexSourceWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as JPBPriceIndexSourceWindowParams | undefined;
   const selectedSource = JPBManager.normalizePriceIndexSource(params?.source);
   const onConfirm = params?.onConfirm;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canViewDebugTable =
      hasAccessToFeature(Feature.Debugging) || hasAccessToFeature(Feature.JPBIndexTool);

   const [draftSource, setDraftSource] = useState<JPBPriceIndexSource>(selectedSource);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [tablesBySource, setTablesBySource] = useState<SourceTableMap>({
      [JPBPriceIndexSource.AgroCura]: null,
      [JPBPriceIndexSource.Molbak]: null,
      [JPBPriceIndexSource.None]: null
   });
   const [selectedLandsdel, setSelectedLandsdel] = useState<string>("");

   useEffect(() => {
      setDraftSource(selectedSource);
   }, [selectedSource, props.managedWindow.updateCounter]);

   useEffect(() => {
      let cancelled = false;
      setLoading(true);
      setError(null);
      Promise.all(
         SOURCE_OPTIONS.map(async (option) => {
            const table = await JPBManager.getPriceIndexFactorsTable(option.value);
            return [option.value, table] as const;
         })
      )
         .then((entries) => {
            if (cancelled) return;
            const nextMap: SourceTableMap = {
               [JPBPriceIndexSource.AgroCura]: null,
               [JPBPriceIndexSource.Molbak]: null,
               [JPBPriceIndexSource.None]: null
            };
            entries.forEach(([source, table]) => {
               nextMap[source] = table;
            });
            setTablesBySource(nextMap);
         })
         .catch((err: any) => {
            if (cancelled) return;
            setError(err?.message || String(err));
            setTablesBySource({
               [JPBPriceIndexSource.AgroCura]: null,
               [JPBPriceIndexSource.Molbak]: null,
               [JPBPriceIndexSource.None]: null
            });
         })
         .finally(() => {
            if (cancelled) return;
            setLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, []);

   const landsdelOptions = useMemo(() => {
      const names = new Set<string>();
      SOURCE_OPTIONS.forEach((option) => {
         (tablesBySource[option.value]?.rows || []).forEach((row) => names.add(row.landsdel));
      });
      return [...names].sort((a, b) => a.localeCompare(b, "da"));
   }, [tablesBySource]);

   useEffect(() => {
      if (!landsdelOptions.length) {
         setSelectedLandsdel("");
         return;
      }
      if (selectedLandsdel && landsdelOptions.includes(selectedLandsdel)) return;

      const defaultFromSource = (tablesBySource[draftSource]?.rows || [])[0]?.landsdel;
      setSelectedLandsdel(defaultFromSource || landsdelOptions[0]);
   }, [landsdelOptions, selectedLandsdel, tablesBySource, draftSource]);

   const chartRows = useMemo<PseudonymizedChartRow[]>(() => {
      if (!selectedLandsdel) return [];

      const periodSet = new Set<string>();
      [JPBPriceIndexSource.AgroCura, JPBPriceIndexSource.Molbak].forEach((source) => {
         const row = tablesBySource[source]?.rows.find((item) => item.landsdel === selectedLandsdel);
         if (!row) return;
         Object.keys(row.factorsByPeriod).forEach((periodKey) => periodSet.add(periodKey));
      });

      if (!periodSet.size) return [];

      const periodKeys = [...periodSet].sort((a, b) => {
         const aSort = toPeriodSortValue(a);
         const bSort = toPeriodSortValue(b);
         if (aSort !== bSort) return aSort - bSort;
         return a.localeCompare(b);
      });

      const rowBySource = {
         [JPBPriceIndexSource.None]: tablesBySource[JPBPriceIndexSource.None]?.rows.find((item) => item.landsdel === selectedLandsdel),
         [JPBPriceIndexSource.Molbak]: tablesBySource[JPBPriceIndexSource.Molbak]?.rows.find((item) => item.landsdel === selectedLandsdel),
         [JPBPriceIndexSource.AgroCura]: tablesBySource[JPBPriceIndexSource.AgroCura]?.rows.find((item) => item.landsdel === selectedLandsdel)
      };

      return periodKeys.map((periodKey) => {
         const molbakFactor = rowBySource[JPBPriceIndexSource.Molbak]?.factorsByPeriod[periodKey] ?? null;
         const agroCuraFactor = rowBySource[JPBPriceIndexSource.AgroCura]?.factorsByPeriod[periodKey] ?? null;
         return {
            periodKey,
            [JPBPriceIndexSource.None]: toPseudoFactor(1),
            [JPBPriceIndexSource.Molbak]: toPseudoFactor(molbakFactor),
            [JPBPriceIndexSource.AgroCura]: toPseudoFactor(agroCuraFactor)
         };
      });
   }, [selectedLandsdel, tablesBySource]);

   const activeSourceTable = tablesBySource[draftSource];
   const activeRows = activeSourceTable?.rows ?? [];
   const activePeriodKeys = activeSourceTable?.periodKeys ?? [];
   const isMolbak = draftSource === JPBPriceIndexSource.Molbak;

   const hasChartData = useMemo(() => {
      return chartRows.some((row) =>
         [JPBPriceIndexSource.None, JPBPriceIndexSource.Molbak, JPBPriceIndexSource.AgroCura]
            .some((source) => typeof row[source] === "number" && Number.isFinite(row[source] as number))
      );
   }, [chartRows]);

   const publicEmptyMessage = useMemo(() => {
      if (!selectedLandsdel) {
         return Localization.getText("No landsdel available.");
      }
      if (!hasChartData) {
         return Localization.getText("No index curves available for selected landsdel.");
      }
      return null;
   }, [selectedLandsdel, hasChartData]);

   const handleClose = () => {
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   const handleApply = () => {
      onConfirm?.(draftSource);
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("Price index source")}
         variant="Big"
         window={props.managedWindow}
         onClose={handleClose}
         PosDefault={{
            left: "50%",
            top: "48%",
            transform: "translate(-50%, -50%)",
            width: "900px",
            height: "560px"
         }}
      >
         <ADModalBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
               <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label className="GlassInputLabel" style={{ minWidth: 140 }}>
                     {Localization.getText("Applied price index source")}
                  </label>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                     {SOURCE_OPTIONS.map((option) => (
                        <GlassButton
                           key={option.value}
                           onClick={() => setDraftSource(option.value)}
                           style={{
                              opacity: draftSource === option.value ? 1 : 0.65,
                              outline: draftSource === option.value ? "1px solid rgba(255,255,255,0.5)" : "none"
                           }}
                        >
                           {option.label}
                        </GlassButton>
                     ))}
                  </div>
               </div>

               <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <label className="GlassInputLabel" style={{ minWidth: 140 }}>
                     {Localization.getText("Landsdel")}
                  </label>
                  <select
                     value={selectedLandsdel}
                     onChange={(e) => setSelectedLandsdel(e.target.value)}
                     style={{ minWidth: 260 }}
                     disabled={loading || landsdelOptions.length === 0}
                  >
                     {landsdelOptions.map((landsdel) => (
                        <option key={landsdel} value={landsdel}>
                           {landsdel}
                        </option>
                     ))}
                  </select>
               </div>

               <div style={{ fontSize: "0.9em", color: "rgba(255,255,255,0.75)" }}>
                  {Localization.getText("Pseudonymized preview. Exact hover values are hidden.")}
               </div>

               {loading && <div>{Localization.getText("Updating...")}</div>}
               {!loading && error && <div className="PropertyWarning">{error}</div>}
               {!loading && !error && publicEmptyMessage && <div>{publicEmptyMessage}</div>}

               {!loading && !error && !publicEmptyMessage && (
                  <div style={{ width: "100%", height: 320, border: "1px solid rgba(255,255,255,0.12)", padding: 6 }}>
                     <ResponsiveContainer>
                        <ComposedChart data={chartRows} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                           <CartesianGrid strokeDasharray="3 3" />
                           <XAxis dataKey="periodKey" minTickGap={20} />
                           <YAxis tickFormatter={(value) => Number(value).toFixed(1)} />
                           <Legend />
                           <Line
                              type="monotone"
                              dataKey={JPBPriceIndexSource.None}
                              name={JPBManager.getPriceIndexSourceLabel(JPBPriceIndexSource.None)}
                              stroke="#616161"
                              strokeWidth={2}
                              dot={false}
                              activeDot={false}
                              connectNulls={true}
                           />
                           <Line
                              type="monotone"
                              dataKey={JPBPriceIndexSource.Molbak}
                              name={JPBManager.getPriceIndexSourceLabel(JPBPriceIndexSource.Molbak)}
                              stroke="#7b1fa2"
                              strokeWidth={2}
                              dot={false}
                              activeDot={false}
                              connectNulls={false}
                           />
                           <Line
                              type="monotone"
                              dataKey={JPBPriceIndexSource.AgroCura}
                              name={JPBManager.getPriceIndexSourceLabel(JPBPriceIndexSource.AgroCura)}
                              stroke="#00796b"
                              strokeWidth={2}
                              dot={false}
                              activeDot={false}
                              connectNulls={false}
                           />
                        </ComposedChart>
                     </ResponsiveContainer>
                  </div>
               )}

               {canViewDebugTable && (
                  <>
                     <div style={{ marginTop: 4, fontSize: "0.85em", color: "rgba(255,255,255,0.8)" }}>
                        {Localization.getText("Debug table (exact factors)")}
                     </div>
                     {!loading && !error && (
                        <div style={{ overflowX: "auto", maxHeight: "240px", overflowY: "auto" }}>
                           <table className="table table-sm table-striped" style={{ minWidth: 720 }}>
                              <thead>
                                 <tr>
                                    <th>{Localization.getText("Landsdel")}</th>
                                    {isMolbak && <th>{Localization.getText("Model")}</th>}
                                    {isMolbak && <th>{Localization.getText("Avg tx count")}</th>}
                                    {activePeriodKeys.map((periodKey) => (
                                       <th key={periodKey} style={{ textAlign: "right" }}>{periodKey}</th>
                                    ))}
                                 </tr>
                              </thead>
                              <tbody>
                                 {activeRows.map((row) => (
                                    <tr key={row.landsdel}>
                                       <td>{row.landsdel}</td>
                                       {isMolbak && <td>{row.usesFittedModel ? Localization.getText("Fitted") : Localization.getText("Median")}</td>}
                                       {isMolbak && <td style={{ textAlign: "right" }}>{row.avgTxCount === null ? "-" : row.avgTxCount.toFixed(1)}</td>}
                                       {activePeriodKeys.map((periodKey) => (
                                          <td key={`${row.landsdel}-${periodKey}`} style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                             {formatFactor(row.factorsByPeriod[periodKey] ?? null)}
                                          </td>
                                       ))}
                                    </tr>
                                 ))}
                              </tbody>
                           </table>
                        </div>
                     )}
                  </>
               )}
            </div>
         </ADModalBody>
         <ADModalFooter>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
               <GlassButton onClick={handleClose}>{Localization.getText("Cancel")}</GlassButton>
               <GlassButton onClick={handleApply}>{Localization.getText("OK")}</GlassButton>
            </div>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
